import React, { useEffect, useState, useMemo } from "react";
import InputField from "./InputField";
import {
  PAYMENT_METHODS,
  API_PAYMENT_METHODS,
  AddAccountFormType,
  AddCryptoWalletFormType,
  AddUpiAccountFormType,
  WithdrawFormType,
  addAccountFormArr,
  addCryptoWalletFormArr,
  addUpiFormArr,
  cryptoCurrencyList,
  withdrawFormArr,
  useAppSelector,
  PaymentMethodObjType,
  JwtToken,
  getFieldFromToken,
} from "@hypexone/uicore";
import Button from "../../components/shared/Button/Button";
import {  paymentClient } from "../../../App.utils";
import { CurrencyListDto } from "../Payments/commonutils";


interface WithdrawFormFieldsProps {
  withdrawFormData: WithdrawFormType;
  handleWithdrawFormChange: React.ChangeEventHandler<HTMLInputElement>;
  onSave: (e: React.FormEvent<HTMLFormElement>) => void;
  isWithdrawSaveBtnDisabled: boolean;
  loading: boolean;
  getReceivableAmount: Function;
  receivableAmount: number;
  currentSelectedAccount: PaymentMethodObjType;
  paymentProvider?: string;
  cryptoAmount?: string;
  setCryptoAmount?: (amount: string) => void;
  exchangeRate?: number | null;
  minTxnAmount?: number;
}

export const WithdrawFormFields: React.FC<WithdrawFormFieldsProps> = ({
  withdrawFormData,
  handleWithdrawFormChange,
  isWithdrawSaveBtnDisabled,
  onSave,
  loading,
  getReceivableAmount,
  receivableAmount,
  currentSelectedAccount,
  paymentProvider,
  cryptoAmount,
  setCryptoAmount,
  exchangeRate,
  minTxnAmount = 0,
}) => {
  const { langData } = useAppSelector((state) => state.cms);
  const isCryptoPayment = currentSelectedAccount?.paymentMethod === "CRYPTO_WALLET_TRANSFER" ||
    paymentProvider?.toLowerCase() === "payzlncrypto" ||
    paymentProvider?.toLowerCase() === "payzlncryptoseamless";

  const convertedPointsAmount = useMemo(() => {
    if (exchangeRate && cryptoAmount && Number(cryptoAmount) > 0) {
      return (Number(cryptoAmount) * exchangeRate).toFixed(2);
    }
    return "";
  }, [exchangeRate, cryptoAmount]);

  return (
    <form
      onSubmit={onSave}
      className="flex flex-col mt-4 md:ml-4 w-full md:w-[50%] text-base leading-none text-black"
    >
      <div className="text-xs text-red-500 mb-4">
        Note : Please allow 72hrs for withdrawal amount to credit, in case of
        any further delay reach out to customer care.
      </div>
      {isCryptoPayment && currentSelectedAccount?.paymentMethod === "CRYPTO_WALLET_TRANSFER" ? (
        <>
          <div>
            <label className="block text-sm font-medium mb-1">
              Enter Crypto Amount ({currentSelectedAccount?.paymentMethodDetails?.cryptoCurrency})
            </label>
            <input
              value={cryptoAmount || ""}
              onChange={(e) => setCryptoAmount?.(e.target.value)}
              className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={`Enter ${currentSelectedAccount?.paymentMethodDetails?.cryptoCurrency} Amount`}
              required
              type="number"
              step="any"
            />
          </div>
          {convertedPointsAmount && (
            <div className="mt-3">
              <label className="block text-sm font-medium mb-1">
                Converted Amount (Points/INR)
              </label>
              <input
                value={convertedPointsAmount}
                disabled
                className="block w-full p-2 border border-gray-300 bg-gray-100 rounded-md"
              />
            </div>
          )}
        </>
      ) : (
        withdrawFormArr.map((field) => {
          const { key, label, langKey, placeholder, type } = field;
          return (
            <React.Fragment key={key}>
              <InputField
                label={langData?.[langKey]}
                key={label}
                value={withdrawFormData[key]}
                id={key}
                type={type}
                placeholder={langData?.[placeholder]}
                onChange={handleWithdrawFormChange}
                disabled={receivableAmount !== null}
              />
            </React.Fragment>
          );
        })
      )}
      {isCryptoPayment && currentSelectedAccount?.paymentMethod === "CRYPTO_WALLET_TRANSFER" && convertedPointsAmount && (
        <div className="p-2 border bg-slate-300 my-3 rounded text-center">
          {langData?.["receivable_crypto_amount"] || "Receivable Amount"}: {convertedPointsAmount} {currentSelectedAccount?.paymentMethodDetails?.cryptoCurrency}
        </div>
      )}
      {(paymentProvider?.toLowerCase() === "payzlncrypto" || paymentProvider?.toLowerCase() === "payzlncryptoseamless") ? (
        <>
          {!receivableAmount && (
            <Button
              type="button"
              className={`gap-2.5 px-4 py-2 mt-6 text-base text-center text-white ${!isWithdrawSaveBtnDisabled ? "bg-blue-600" : "bg-blue-400"} rounded border border-blue-600 border-solid min-h-[38px]`}
              loading={loading}
              onClick={() => getReceivableAmount()}
            >
              {langData?.["next"]}
            </Button>
          )}

          {receivableAmount && (
            <>
              <div className="p-2 border bg-slate-300 my-3 rounded text-center">
                {langData?.["receivable_crypto_amount"]}: {receivableAmount + " " + currentSelectedAccount?.paymentMethodDetails?.cryptoCurrency}
              </div>
              <Button
                type="submit"
                className={`gap-2.5 px-4 py-2 mt-2 text-base text-center text-white ${!isWithdrawSaveBtnDisabled ? "bg-blue-600" : "bg-blue-400"} rounded border border-blue-600 border-solid min-h-[38px]`}
                loading={loading}
              >
                {langData?.["submit"]}
              </Button>
            </>
          )}
        </>
      ) :
        <>
          <Button
            type="submit"
            className={`gap-2.5 px-4 py-2 mt-2 text-base text-center text-white ${!isWithdrawSaveBtnDisabled ? "bg-blue-600" : "bg-blue-400"} rounded border border-blue-600 border-solid min-h-[38px]`}
            loading={loading}
          >
            {langData?.["submit"]}
          </Button>
        </>
      }
    </form>
  );
};

interface AddAccountFieldsProps {
  resetForms: () => void;
  onSave: (e: React.FormEvent<HTMLFormElement>) => void;
  addAccountFormData: AddAccountFormType;
  handleAddAccountFormChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  isSaveBtnDisabled: boolean;
  handleAddUpiAccountFormChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  addUpiAccountFormData: AddUpiAccountFormType;
  selectedPaymentMethod: PAYMENT_METHODS | API_PAYMENT_METHODS;
  addCryptoWalletFormData: AddCryptoWalletFormType;
  setAddCryptoWalletFormData: any;
  loading: boolean;
  otpLoader: boolean;
  otpTimer: number;
  phoneNumbeErrorMsg: string;
  sendOtp: (isWhatsapp?: boolean) => void | Promise<void>;
  showSmsOtpButton: boolean;
  showWhatsappOtpButton: boolean;
  selectedPaymentProvider?: string;
  withdrawCryptoCurrencies?: any[];
}

export const AddAccountFormFields: React.FC<AddAccountFieldsProps> = ({
  addAccountFormData,
  addUpiAccountFormData,
  handleAddAccountFormChange,
  handleAddUpiAccountFormChange,
  isSaveBtnDisabled,
  onSave,
  resetForms,
  selectedPaymentMethod,
  addCryptoWalletFormData,
  setAddCryptoWalletFormData,
  loading,
  otpLoader,
  otpTimer,
  phoneNumbeErrorMsg,
  sendOtp,
  showSmsOtpButton,
  showWhatsappOtpButton,
  selectedPaymentProvider,
  withdrawCryptoCurrencies = [],
}) => {
  const { langData } = useAppSelector((state) => state.cms);
  const [currencyList, setCurrencyList] = useState<CurrencyListDto[]>([]);
  const isPgman = selectedPaymentProvider?.toLowerCase() === "pgman";
  const isOnlineUser = getFieldFromToken(JwtToken.MODE)?.toLowerCase() === 'online';

  const handleSelectFieldChange = (event: any) => {
    const selectedValue = event.target.value;
    if (isPgman) {
      const selected = withdrawCryptoCurrencies.find(
        (crypto) => (crypto.currency + crypto.blockchain) === selectedValue
      );
      if (selected) {
        addCryptoWalletFormData.selectedCryptoCurrency = {
          crypto_currency: selected.currency,
          blockchain: selected.blockchain,
          network_id: selected.network_id || selected.networkId,
          token_type: selected.tokenType || "",
          token_address: selected.tokenAddress || "",
        } as any;
      }
      setAddCryptoWalletFormData({ ...addCryptoWalletFormData });
      return;
    }
    const selected = currencyList.find(
      (crypto) => crypto.crypto_currency + crypto.blockchain === selectedValue
    );
    addCryptoWalletFormData.selectedCryptoCurrency = selected;
    setAddCryptoWalletFormData({ ...addCryptoWalletFormData });
  };

  const handleAddCryptoWalletFormChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (event.target.tagName === "SELECT") {
      handleSelectFieldChange(event);
    } else {
      addCryptoWalletFormData[event.target.id] = event.target.value;
      setAddCryptoWalletFormData({ ...addCryptoWalletFormData });
    }
  };

  const getCurrencyList = async () => {
    try {
      let paymentProvider = selectedPaymentProvider?.toLowerCase() === "payzlncrypto"
        ? "payzln-crypto"
        : selectedPaymentProvider?.toLowerCase() === "payzlncryptoseamless" ? "payzln-crypto-seamless" : selectedPaymentProvider;
      const response = await paymentClient.getCurrencyList(paymentProvider);
      if (response.status === 200) {
        setCurrencyList(response?.data?.crypto_currency);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (selectedPaymentMethod === PAYMENT_METHODS.CRYPTO_WALLET_TRANSFER && !isPgman) {
      getCurrencyList();
    }
  }, [selectedPaymentMethod, selectedPaymentProvider]);

  return (
    <form
      onSubmit={onSave}
      className="flex flex-col mt-4 md:ml-4 w-full md:w-[50%] text-base leading-none text-black"
    >
      {selectedPaymentMethod === PAYMENT_METHODS.BANK_TRANSFER &&
        addAccountFormArr.map((field) => {
          const { key, label, langKey, placeholder, type } = field;
          return (
            <React.Fragment key={key}>
              <InputField
                label={langData?.[langKey]}
                key={label}
                value={addAccountFormData[key]}
                id={key}
                type={type}
                placeholder={langData?.[placeholder]}
                onChange={handleAddAccountFormChange}
              />
            </React.Fragment>
          );
        })}
      {selectedPaymentMethod === API_PAYMENT_METHODS.UPI &&
        addUpiFormArr.map((field) => {
          const { key, label, langKey, placeholder, type } = field;
          return (
            <React.Fragment key={key}>
              <InputField
                label={langData?.[langKey]}
                key={label}
                value={addUpiAccountFormData[key]}
                id={key}
                type={type}
                placeholder={langData?.[placeholder]}
                onChange={handleAddUpiAccountFormChange}
              />
            </React.Fragment>
          );
        })}
      {selectedPaymentMethod === PAYMENT_METHODS.CRYPTO_WALLET_TRANSFER && (
        <>
          <div>
            <label htmlFor={"crypto-select"} className="self-start">
              {langData?.["select_currency"]}
            </label>
            <select
              id="crypto-select"
              className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
              onChange={handleAddCryptoWalletFormChange}
              defaultValue={
                addCryptoWalletFormData?.selectedCryptoCurrency
                  ? addCryptoWalletFormData.selectedCryptoCurrency
                    .crypto_currency
                  : ""
              }
            >
              <option value="" disabled>
                {langData?.["select_a_crypto"]}
              </option>
              {isPgman
                ? withdrawCryptoCurrencies.map((crypto, index) => {
                    return (
                      <option key={index} value={crypto.currency + crypto.blockchain}>
                        {crypto.currency}({crypto.blockchain})
                      </option>
                    );
                  })
                : currencyList.map((crypto, index) => {
                    return (
                      <option
                        key={index}
                        value={crypto.crypto_currency + crypto.blockchain}
                      >
                        {crypto.crypto_currency + " (" + crypto.blockchain + ")"}
                      </option>
                    );
                  })}
            </select>
          </div>
          <div className="mt-3">
            {addCryptoWalletFormArr.map((field) => {
              const { key, label, langKey, placeholder, type } = field;
              return (
                <React.Fragment key={key}>
                  <InputField
                    label={langData?.[langKey]}
                    key={label}
                    value={addCryptoWalletFormData[key]}
                    id={key}
                    type={type}
                    placeholder={langData?.[placeholder]}
                    onChange={handleAddCryptoWalletFormChange}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
      {isOnlineUser &&
        <div className="flex flex-col md:flex-row md:flex-nowrap md:items-end gap-3 mt-2 w-full min-w-0">
          <InputField
            label={langData?.["otp"]}
            key={"otp"}
            value={selectedPaymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? addAccountFormData["otp"] : selectedPaymentMethod === API_PAYMENT_METHODS.UPI ? addUpiAccountFormData["otp"] : addCryptoWalletFormData["otp"]}
            id={"otp"}
            type={"number"}
            placeholder={langData?.["otp"]}
            onChange={selectedPaymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? handleAddAccountFormChange : selectedPaymentMethod === API_PAYMENT_METHODS.UPI ? handleAddUpiAccountFormChange : handleAddCryptoWalletFormChange}
            width="w-full md:max-w-[200px] md:shrink-0"
          />
          <div className="mb-2 flex flex-col gap-2 min-w-0 w-full md:flex-1">
            <div className="flex flex-row flex-nowrap items-stretch gap-2 w-full md:w-auto">
              {showSmsOtpButton && (
                <Button
                  className={`flex-1 min-w-0 md:flex-initial gap-2 px-3 py-2 text-sm whitespace-nowrap text-center text-white ${!otpLoader ? "bg-slate-700" : "bg-slate-500"} rounded min-h-[38px] md:shrink-0`}
                  loading={otpLoader}
                  onClick={() => sendOtp(false)}
                  disabled={otpLoader || otpTimer > 0}
                >
                  {langData?.["send_otp"]}
                </Button>
              )}
              {showWhatsappOtpButton && (
                <Button
                  className={`flex-1 min-w-0 md:flex-initial gap-2 px-3 py-2 text-sm whitespace-nowrap text-center text-white ${!otpLoader ? "bg-teal-700" : "bg-teal-500"} rounded min-h-[38px] md:shrink-0`}
                  loading={otpLoader}
                  onClick={() => sendOtp(true)}
                  disabled={otpLoader || otpTimer > 0}
                >
                  {langData?.["get_otp_whatsapp"] ?? "WhatsApp OTP"}
                </Button>
              )}
            </div>
            {otpTimer > 0 && (
              <div className="text-red-500 text-sm">
                {langData?.['resend_in_txt'] + ' ' + otpTimer + 's'}
              </div>
            )}
            {phoneNumbeErrorMsg && (
              <div className="text-red-500 text-sm">{phoneNumbeErrorMsg}</div>
            )}
          </div>
        </div>
      }
      <Button
        type="submit"
        className={`gap-2.5 self-stretch px-4 py-2 mt-6 text-base text-center text-white ${!isSaveBtnDisabled ? "bg-blue-600" : "bg-blue-400"
          } rounded border border-blue-600 border-solid min-h-[38px]`}
        loading={loading}
      >
        {langData?.["save_changes"]}
      </Button>
    </form>
  );
};
